import { useCallback, useEffect, useReducer } from 'react'
import { fillProps, filterEntries, isEmpty } from 'utils/index'

const removeFalsy = filterEntries((key, value) => !!value)

// 외부구독자에 밸리데이션이 실행되었음을 알리기 위한 카운터
export const state = { count: 0 }

const useValidator = (validator = err => err, revalidationOnFailsDeps = []) => {
  const [errors, dispatch] = useReducer((oldErrors, { type, payload }) => {
    switch (type) {
      case 'validate': {
        try {
          const validationResult = removeFalsy(
            []
              .concat(validator)
              .reduce(
                (result, validatorFunc) => validatorFunc(result),
                oldErrors
              )
          )
          payload(resolve => resolve(validationResult))
          return validationResult
        } catch (errorWhileValidation) {
          payload((resolve, reject) => reject(errorWhileValidation))
          return {
            ...oldErrors,
            errorWhileValidation,
          }
        }
      }

      case 'revalidate': {
        try {
          if (isEmpty(Object.keys(oldErrors))) return oldErrors
          const updatingErrors = []
            .concat(validator)
            .reduce((result, validatorFunc) => validatorFunc(result), oldErrors)
          return removeFalsy(fillProps(updatingErrors)(oldErrors))
        } catch (errorWhileValidation) {
          return {
            ...oldErrors,
            errorWhileValidation,
          }
        }
      }

      case 'add': {
        return { ...oldErrors, ...payload }
      }

      case 'clear': {
        if (payload === undefined) {
          return isEmpty(oldErrors) ? oldErrors : {}
        }
        const { [payload]: omit, ...newErrors } = oldErrors
        return newErrors
      }

      default: {
        return oldErrors
      }
    }
  }, {})

  const validate = useCallback(async () => {
    const newErrors = await new Promise((resolve, reject) => {
      dispatch({
        type: 'validate',
        payload: callback => {
          state.count++
          callback(resolve, reject)
        },
      })
    })
    if (!isEmpty(filterEntries((key, value) => value)(newErrors))) {
      throw newErrors
    }
  }, [dispatch])

  useEffect(() => {
    dispatch({ type: 'revalidate' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ...revalidationOnFailsDeps])

  const clearError = useCallback(
    name =>
      dispatch({
        type: 'clear',
        payload: name,
      }),
    [dispatch]
  )

  const addError = useCallback(
    errorMap =>
      dispatch({
        type: 'add',
        payload: errorMap,
      }),
    [dispatch]
  )

  return {
    validate,
    errors,
    addError,
    clearError,
  }
}

export default useValidator
